import React, { useState } from 'react';
import { getUserProfile, deleteUser, UserProfileResponse, DeleteUserRequest } from '../../api/user-admin';

const UserManagement = () => {
    const [userId, setUserId] = useState('');
    const [userData, setUserData] = useState<UserProfileResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteReason, setDeleteReason] = useState('Requested in app');

    const handleSearch = async () => {
        if (!userId.trim()) {
            setError('Please enter a user ID');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setUserData(null);

        try {
            const response = await getUserProfile(userId.trim());
            setUserData(response);
            setSuccess(`Found user: ${response.email || 'No email'}`);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async () => {
        if (!userData || !deleteReason.trim()) {
            setError('Please provide a reason for deletion');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const request: DeleteUserRequest = {
                reason: deleteReason.trim()
            };

            const response = await deleteUser(userData.userId, request);
            setSuccess(`Successfully deleted user ${response.userEmail} (${response.deletedUserId})`);
            setUserData(null);
            setShowDeleteConfirm(false);
            setDeleteReason('Requested in app');
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = () => {
        if (!userData) return;
        setShowDeleteConfirm(true);
    };

    const cancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeleteReason('Requested in app');
    };

    return (
        <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">User Management</h2>
                
                {/* Search Section */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 text-gray-700">Search User</h3>
                    <div className="flex space-x-2">
                        <input
                            type="text"
                            value={userId}
                            onChange={(e) => setUserId(e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter user ID"
                            disabled={loading}
                        />
                        <button
                            onClick={handleSearch}
                            disabled={loading || !userId.trim()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Searching...' : 'Search'}
                        </button>
                    </div>
                </div>

                {/* User Data Display */}
                {userData && (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold mb-4 text-gray-700">User Information</h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">User ID</label>
                                    <p className="text-sm text-gray-900 font-mono">{userData.userId}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Email</label>
                                    <p className="text-sm text-gray-900">{userData.email || 'No email'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600">Balance</label>
                                    <p className="text-sm text-gray-900">{userData.balance} points</p>
                                </div>
                            </div>

                            {userData.profile && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-2">Profile Data</label>
                                    <div className="bg-white border rounded p-3">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <span className="text-xs text-gray-500">First Name:</span>
                                                <p className="text-sm">{userData.profile.firstName || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Last Name:</span>
                                                <p className="text-sm">{userData.profile.lastName || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Street Address:</span>
                                                <p className="text-sm">{userData.profile.streetAddress || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Postcode:</span>
                                                <p className="text-sm">{userData.profile.postcode || 'Not set'}</p>
                                            </div>
                                            <div>
                                                <span className="text-xs text-gray-500">Country Code:</span>
                                                <p className="text-sm">{userData.profile.countryCode || 'Not set'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={confirmDelete}
                                    disabled={loading}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Delete User
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4 text-red-600">Confirm User Deletion</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                Are you sure you want to delete user <strong>{userData?.email || userData?.userId}</strong>? 
                                This action cannot be undone and will permanently delete their Firebase account.
                            </p>
                            
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Reason for deletion *
                                </label>
                                <textarea
                                    value={deleteReason}
                                    onChange={(e) => setDeleteReason(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    placeholder="Enter reason for deletion..."
                                    rows={3}
                                    disabled={loading}
                                />
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={cancelDelete}
                                    disabled={loading}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteUser}
                                    disabled={loading || !deleteReason.trim()}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Deleting...' : 'Delete User'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Messages */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-green-800">{success}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
